import '../Authorization.css';
import React, { useState } from 'react';
import Login from './Login';
import Verification from './Verification';
import AppHeader from './AppHeader';
import { FORM_PATH, MAX_SEND_OTP_ERROR, MAX_VERIFY_OTP_ERROR } from '../consts/consts';
import { fetchFormData, validateCode, verifyPatientAndSendOtp } from '../api/AuthorizationApi';
import { InputType } from '../utils/InputType';
import { v4 as uuidv4 } from 'uuid';
import { useLanguage } from '../LanguageContext';
import { getTranslation } from '../consts/translations';
import { getStoredUuidPathSegment } from '../utils/urlUtils';
import { appendLangForLanguage, languageFromCode } from '../utils/languageMapping';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import { base64DecodeToJson } from '../utils/bas64Utils';
import { FormMetadata } from '../types';
import { initLogger } from '../Logger';

interface AuthorizationProps {
  inputPhoneOrEmail: string;
  setInputPhoneOrEmail: (phoneOrEmail: string) => void;
  showVerification: boolean;
  setShowVerification: (show: boolean) => void;
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  onExit: () => void;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  urlParams: Record<string, string>;
  setFormData: (value: FormMetadata) => void;
  logout: () => void;
  verificationError: string;
  setVerificationError: (error: string) => void;
}

const Authorization: React.FC<AuthorizationProps> = (props: AuthorizationProps) => {
  const [inputType, setInputType] = useState<InputType>(InputType.Email);
  const [otp, setOtp] = useState('');
  const [validateButtonLoading, setValidateButtonLoading] = useState(false);
  const [sendButtonLoading, setSendButtonLoading] = useState(false);
  const [sendAgainClicked, setSendAgainClicked] = useState(true);
  const { language, changeLanguage } = useLanguage();
  const translation = getTranslation(language);
  const navigate = useNavigate();

  const clickExit = () => {
    props.setVerificationError('');
    props.setShowModal(false);
    props.onExit();
  };

  const handleSendVerificationCode = async () => {
    props.setVerificationError('');
    setSendButtonLoading(true);
    try {
      let sessionId = props.sessionId;
      if (!props.sessionId) {
        const newSessionId = uuidv4();
        props.setSessionId(newSessionId);
        sessionId = newSessionId;
      }
      await verifyPatientAndSendOtp(
        props.inputPhoneOrEmail,
        inputType,
        sessionId,
        translation,
        language,
      );
      props.setShowVerification(true);
      setSendAgainClicked(true);
    } catch (error: unknown) {
      setSendButtonLoading(false);
      if (error instanceof Error && error.message === MAX_SEND_OTP_ERROR) {
        props.setVerificationError('');
        props.setShowModal(true);
        return;
      }
      props.setVerificationError(
        error instanceof Error ? error.message : translation.general_sever_error,
      );
      setSendAgainClicked(false);
    } finally {
      setSendButtonLoading(false);
    }
  };

  const handleValidateCode = async () => {
    props.setVerificationError('');
    setValidateButtonLoading(true);
    try {
      await validateCode(
        props.inputPhoneOrEmail,
        otp,
        inputType,
        props.sessionId,
        translation,
        language,
      );
      const uuid = getStoredUuidPathSegment();
      const response = await fetchFormData(translation, props.logout, uuid);
      const decodedFormData = base64DecodeToJson(response.form_data);
      const resolvedLang = languageFromCode(response.language);
      changeLanguage(resolvedLang, uuid);
      props.setFormData(decodedFormData);
      navigate(appendLangForLanguage(`${FORM_PATH}/${uuid}`, resolvedLang));
      initLogger();
    } catch (error: unknown) {
      setValidateButtonLoading(true);
      if (error instanceof Error && error.message === MAX_VERIFY_OTP_ERROR) {
        props.setVerificationError('');
        props.setShowModal(true);
        return;
      }
      props.setVerificationError(
        error instanceof Error ? error.message : translation.general_sever_error,
      );
    } finally {
      setValidateButtonLoading(false);
      setSendAgainClicked(false);
    }
  };

  return (
    <div>
      <div className='authorization-view'>
        <div className='authorization-background-image' />
        <AppHeader
          title={translation.login_title}
          subtitle={translation.login_subtitle}
          height='250px'
          showTitle={!props.showModal}
        />
        {props.showModal ? (
          <div className='login-modal'>
            <div className='login-modal-content'>
              <p>{translation.check_input_modal_text}</p>
              <a href='mailto:acmg.support@psifas.org.il'>acmg.support@psifas.org.il</a>
            </div>
          </div>
        ) : !props.showVerification ? (
          <Login
            inputPhoneOrEmail={props.inputPhoneOrEmail}
            setInputPhoneOrEmail={props.setInputPhoneOrEmail}
            onSendCode={handleSendVerificationCode}
            loading={sendButtonLoading}
            setShowVerification={props.setShowVerification}
            verificationError={props.verificationError}
            setVerificationError={props.setVerificationError}
            setInputType={setInputType}
          />
        ) : (
          <Verification
            setVerificationCode={setOtp}
            onValidateCode={handleValidateCode}
            buttonDisabled={!!otp}
            loading={validateButtonLoading}
            verificationError={props.verificationError}
            sendVerification={handleSendVerificationCode}
            inputPhoneOrEmail={props.inputPhoneOrEmail}
            onExit={clickExit}
            sendAgainClicked={sendAgainClicked}
            setSendAgainClicked={setSendAgainClicked}
          />
        )}
      </div>
      <Footer showExitButton={props.showVerification} onExit={props.logout} />
    </div>
  );
};

export default Authorization;
